import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import authService, { type AuthUser, type AuthResponseData, type ConversionResponseData, type LoginResult } from '../services/authService';
import { reconnectWithToken } from '../services/socket';
import { getApiBaseUrl } from '../services/apiConfig';
import { getCsrfToken } from '../services/apiClient';

export interface UserProfile {
  username: string;
  /** Globally-unique @handle (registered accounts). Distinct from the display-name `username`. */
  handle?: string | null;
  firstName?: string;
  lastName?: string;
  color: string;
  avatarId?: string;
  [key: string]: any;
}

export interface User extends AuthUser {
  id: string;
  role: string;
  profile?: UserProfile;
}

interface UserContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  /**
   * Resolves with `status: '2fa'` (and no session) when the account has a
   * second factor — finish with `completeTwoFactorLogin`.
   */
  login: (email: string, password: string) => Promise<LoginResult>;
  completeTwoFactorLogin: (tempToken: string, code: string) => Promise<AuthResponseData>;
  /** Sign in with a passkey (ADR-0051). Throws `NotAllowedError` when the person cancels. */
  loginWithPasskey: (opts?: { autofill?: boolean; signal?: AbortSignal; onPicked?: () => void }) => Promise<LoginResult>;
  /**
   * Finish a sign-in with an emailed code. Resolves with `status: '2fa'` (and
   * no session) when the account has a second factor.
   */
  loginWithCode: (email: string, code: string) => Promise<LoginResult>;
  /** With `code`, `password` may be empty: the emailed code makes the account. */
  register: (email: string, password: string, username: string, firstName?: string, lastName?: string, code?: string) => Promise<AuthResponseData>;
  /** `redirect` is where to land after the round trip — pass it here rather
   *  than writing `auth_redirect` yourself (see `authService`). */
  loginWithProvider: (provider: string, options?: { redirect?: string }) => void;
  loginAsGuest: (username: string, color: string) => Promise<AuthResponseData>;
  registerGuest: (email: string, password: string, username: string, session?: { roomCode?: string; guestIdentifier?: string }) => Promise<ConversionResponseData>;
  registerGuestWithId: (email: string, password: string, username: string, userId: string, session?: { roomCode?: string; guestIdentifier?: string }) => Promise<ConversionResponseData>;
  logout: () => Promise<boolean>;
  updateProfile: (profileData: Partial<UserProfile>) => Promise<{ user: User } | User>;
  isAuthenticated: boolean;
  isGuest: boolean;
  shouldPromptRegistration: () => boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

/**
 * Optional callback for apps that need to sync user changes to Redux or other stores.
 * Pass this to UserProvider to get notified on user state changes.
 */
export type UserChangeCallback = (user: User | null) => void;

interface UserProviderProps {
  children: ReactNode;
  onUserChange?: UserChangeCallback;
}

const UserProvider: React.FC<UserProviderProps> = ({ children, onUserChange }) => {
  const [user, setUser] = useState<User | null>(authService.getUser() as User | null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Notify parent app of user changes (e.g., for Redux sync)
  const setUserAndNotify = useCallback((newUser: User | null) => {
    setUser(newUser);
    onUserChange?.(newUser);
  }, [onUserChange]);

  useEffect(() => {
    const loadUser = async () => {
      // The session cookie is HttpOnly so we can't peek at it from JS
      // to decide whether to bother calling /api/users/me. Always ask
      // the backend — a cookie set on a sibling subdomain (e.g. an
      // admin who signed in on admin.showliner.io and just opened
      // live.showliner.io) won't have a cached user record on this
      // origin's localStorage but is fully authenticated. Gating on
      // the cached user would render them as logged-out incorrectly.
      // The endpoint returns 401 cheaply for unauthenticated callers
      // so the extra request is fine for public pages.
      try {
        try {
          const userData = await authService.getCurrentUser();
          setUserAndNotify(userData as User);
        } catch (err: any) {
          const status = err.response?.status;
          if (status === 401) {
            // No valid session — clear any stale cache so we render
            // as logged-out instead of flashing a stale username.
            // Silent: no error toast on public pages where this is
            // normal.
            try {
              localStorage.removeItem('auth_user');
            } catch { /* non-fatal */ }
            return;
          }
          if (status !== 404) throw err;
        }
      } catch (err: any) {
        console.error('Error loading user:', err);
        setError('Failed to load user data');
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, [setUserAndNotify]);

  // Keep the in-memory user in sync with same-tab `authService.setUser` calls.
  // The guest identity is established mid-session by the socket `joinSuccess`
  // handler (authService.setUser), not through a context method — without this
  // subscription the context wouldn't learn the visitor became a guest until a
  // remount/refresh, so guest-only UI (e.g. the claim-account banner) failed to
  // appear on first room join.
  useEffect(() => {
    const unsubscribe = authService.onUserChanged((nextUser) => {
      setUserAndNotify(nextUser as User | null);
    });
    return unsubscribe;
  }, [setUserAndNotify]);

  /**
   * Sign in with a password.
   *
   * Returns the raw `LoginResult` so callers can branch: a 2FA-enrolled
   * account resolves with `status: '2fa'` and **no session** — the caller must
   * collect the second factor and finish via `completeTwoFactorLogin`.
   */
  const login = async (email: string, password: string): Promise<LoginResult> => {
    try {
      setError(null);
      const result = await authService.login(email, password);

      if (result.status === '2fa') {
        // Not signed in yet — leave user/socket untouched.
        return result;
      }

      setUserAndNotify(result.data.user as User);

      // Reconnect socket so the new session cookie attaches to the
      // handshake. The token arg is unused under cookie auth.
      reconnectWithToken();

      return result;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Sign in failed');
      throw err;
    }
  };

  const loginWithPasskey = async (opts: { autofill?: boolean; signal?: AbortSignal; onPicked?: () => void } = {}): Promise<LoginResult> => {
    try {
      setError(null);
      const result = await authService.loginWithPasskey(opts);
      if (result.status === 'ok') {
        setUserAndNotify(result.data.user as User);
        reconnectWithToken();
      }
      return result;
    } catch (err: any) {
      setError(err.response?.data?.error || null);
      throw err;
    }
  };

  const loginWithCode = async (email: string, code: string): Promise<LoginResult> => {
    try {
      setError(null);
      const result = await authService.verifyOTP(email, code);
      if (result.status === 'ok') {
        setUserAndNotify(result.data.user as User);
        reconnectWithToken();
      }
      return result;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Verification failed');
      throw err;
    }
  };

  /** Finish a login that stopped at the second factor. */
  const completeTwoFactorLogin = async (tempToken: string, code: string): Promise<AuthResponseData> => {
    try {
      setError(null);
      const data = await authService.verifyTwoFactorLogin(tempToken, code);
      setUserAndNotify(data.user as User);
      reconnectWithToken();
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Verification failed');
      throw err;
    }
  };

  const register = async (email: string, password: string, username: string, firstName?: string, lastName?: string, code?: string): Promise<AuthResponseData> => {
    try {
      setError(null);
      const data = await authService.register(email, password, username, firstName, lastName, code);
      setUserAndNotify(data.user as User);

      // Reconnect socket so the new session cookie attaches to the
      // handshake. The token arg is unused under cookie auth.
      reconnectWithToken();

      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
      throw err;
    }
  };

  const loginWithProvider = (provider: string, options?: { redirect?: string }): void => {
    authService.loginWithProvider(provider, options);
  };

  const loginAsGuest = async (username: string, color: string): Promise<AuthResponseData> => {
    try {
      setError(null);
      const data = await authService.createGuestUser(username, color);
      setUserAndNotify(data.user as User);
      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Guest sign-in failed');
      throw err;
    }
  };

  const registerGuest = async (email: string, password: string, username: string, session?: { roomCode?: string; guestIdentifier?: string }): Promise<ConversionResponseData> => {
    try {
      setError(null);
      if (!user || user.role !== 'guest') {
        const msg = 'You must be logged in as a guest to convert to a registered account';
        setError(msg);
        throw new Error(msg);
      }
      const data = await authService.convertGuestUser(email, password, username, session);
      setUserAndNotify(data.user as User);

      // Reconnect socket so the new session cookie attaches to the
      // handshake. The token arg is unused under cookie auth.
      reconnectWithToken();

      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
      throw err;
    }
  };

  const registerGuestWithId = async (email: string, password: string, username: string, userId: string, session?: { roomCode?: string; guestIdentifier?: string }): Promise<ConversionResponseData> => {
    try {
      setError(null);
      const data = await authService.convertGuestWithId(email, password, username, userId, session);
      setUserAndNotify(data.user as User);

      // Reconnect socket so the new session cookie attaches to the
      // handshake. The token arg is unused under cookie auth.
      reconnectWithToken();

      return data;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Registration failed');
      throw err;
    }
  };

  const logout = async (): Promise<boolean> => {
    try {
      await authService.logout();
      setUserAndNotify(null);
      return true;
    } catch (err: any) {
      setError(err.response?.data?.error || 'Sign out failed');
      return false;
    }
  };

  const updateProfile = async (profileData: Partial<UserProfile>): Promise<{ user: User } | User> => {
    try {
      setError(null);
      const baseUrl = getApiBaseUrl();

      // Cookie attaches via `credentials: 'include'`. The CSRF token comes
      // from getCsrfToken(), which resolves the environment-scoped cookie
      // name exactly as apiClient's axios interceptor does. Never match
      // `showliner_csrf` by a literal name: off production the cookie is
      // suffixed (`showliner_csrf_dev`), a literal finds nothing, and the PUT
      // goes out headerless and is refused as a CSRF mismatch.
      const csrf = getCsrfToken();
      const response = await fetch(`${baseUrl}/api/users/me`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
        },
        body: JSON.stringify({ profile: profileData })
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update profile');
        } else {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
      }

      const data: { user: User } | User = await response.json();
      const updatedUser = ('user' in data) ? (data as { user: User }).user : data as User;
      setUserAndNotify(updatedUser);

      // Sync profile data to localStorage
      if (updatedUser.profile?.avatarId) {
        localStorage.setItem('avatarId', updatedUser.profile.avatarId);
      } else {
        localStorage.removeItem('avatarId');
      }

      if (updatedUser.profile?.username) {
        localStorage.setItem('username', updatedUser.profile.username);
      } else {
        localStorage.removeItem('username');
      }

      if (updatedUser.profile?.color) {
        localStorage.setItem('userColor', updatedUser.profile.color);
      } else {
        localStorage.removeItem('userColor');
      }

      return data;
    } catch (err: any) {
      console.error('Profile update error:', err);
      setError(err.message || 'Profile update failed');
      throw err;
    }
  };

  const shouldPromptRegistration = (): boolean => {
    return user ? user.role === 'guest' : false;
  };

  const isGuest = user ? user.role === 'guest' : false;

  return (
    <UserContext.Provider value={{
      user,
      loading,
      error,
      login,
      completeTwoFactorLogin,
      loginWithPasskey,
      loginWithCode,
      register,
      loginWithProvider,
      loginAsGuest,
      registerGuest,
      registerGuestWithId,
      logout,
      updateProfile,
      isAuthenticated: !!user,
      isGuest,
      shouldPromptRegistration
    }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;
